import React, { useState, useEffect } from 'react';
import { JsonRpc } from 'eosjs';
import { resolveIpfsUrl } from '../constants/gameConfig';

const rpc = new JsonRpc('https://wax.greymass.com', { fetch });

const BADGES = [
  {
    name: 'Boss of Bosses',
    collection: 'syndicates21',
    templateId: '905426',
    mpBoost: 20000
  },
  {
    name: 'Commander Badge',
    collection: 'waxstation21',
    templateId: '902147',
    mpBoost: 10000
  }
];

export default function BadgeClaim({ session, nfts = [], refreshNfts }) {
  const [unclaimedBadges, setUnclaimedBadges] = useState([]);
  const [isChecking, setIsChecking] = useState(false);
  const [claimingId, setClaimingId] = useState(null);

  useEffect(() => {
    checkBadges();
    // eslint-disable-next-line
  }, [nfts, session]);

  const checkBadges = async () => {
    if (!session || !nfts || nfts.length === 0) {
      setUnclaimedBadges([]);
      return;
    }

    setIsChecking(true);
    try {
      // Find all NFTs that match our badge templates
      const badgeNfts = nfts.filter(nft => {
        const tId = nft.template?.template_id;
        return BADGES.some(b => b.templateId === tId);
      });

      if (badgeNfts.length === 0) {
        setUnclaimedBadges([]);
        setIsChecking(false);
        return;
      }

      // Check which ones are already claimed
      const unclaimed = [];
      for (const nft of badgeNfts) {
        const res = await rpc.get_table_rows({
          json: true,
          code: 'whatifdragon',
          scope: 'whatifdragon',
          table: 'claimedbadge',
          lower_bound: nft.asset_id,
          upper_bound: nft.asset_id,
          limit: 1
        });
        
        if (res.rows.length === 0 || res.rows[0].asset_id !== nft.asset_id) {
          unclaimed.push(nft);
        }
      }

      setUnclaimedBadges(unclaimed);
    } catch (e) {
      console.error('Error checking badges:', e);
    }
    setIsChecking(false);
  };

  const handleClaim = async (nft) => {
    if (!session) return;
    setClaimingId(nft.asset_id);
    try {
      const result = await session.transact({
        actions: [
          {
            account: 'whatifdragon',
            name: 'claimbadge',
            authorization: [{ actor: session.actor, permission: session.permission }],
            data: {
              player: session.actor,
              asset_id: nft.asset_id
            }
          }
        ]
      }, {
        blocksBehind: 3,
        expireSeconds: 120
      });

      alert(`Successfully claimed ${nft.name}! Base MP boosted.`);
      await checkBadges();
      if (refreshNfts) await refreshNfts();
    } catch (e) {
      console.error('Claim error', e);
      alert('Error claiming badge: ' + (e.message || e));
    }
    setClaimingId(null);
  };

  if (!session || (!isChecking && unclaimedBadges.length === 0)) return null;

  return (
    <div className="bg-gray-800 border border-yellow-500 rounded-lg p-4 mb-6 shadow-lg">
      <h3 className="text-xl font-bold text-yellow-500 mb-2">🏅 Claimable Badges</h3>
      <p className="text-sm text-gray-300 mb-4">
        You hold NNT badges that grant permanent Mining Power (MP)! Claim them now.
      </p>
      
      {isChecking ? (
        <p className="text-sm text-gray-400">Checking badge status...</p>
      ) : (
        <div className="flex flex-wrap gap-4">
          {unclaimedBadges.map(nft => {
            const badgeInfo = BADGES.find(b => b.templateId === nft.template?.template_id);
            return (
              <div key={nft.asset_id} className="bg-gray-700 p-3 rounded flex items-center gap-4">
                {nft.data?.img && (
                  <img 
                    src={resolveIpfsUrl(nft.data.img)} 
                    alt={nft.name} 
                    className="w-16 h-16 object-contain rounded"
                  />
                )}
                <div>
                  <h4 className="font-bold text-white">{nft.name}</h4>
                  <p className="text-xs text-green-400">+{badgeInfo?.mpBoost} Base MP</p>
                </div>
                <button
                  onClick={() => handleClaim(nft)}
                  disabled={claimingId === nft.asset_id}
                  className="bg-yellow-600 hover:bg-yellow-500 text-white font-bold py-1 px-3 rounded disabled:opacity-50"
                >
                  {claimingId === nft.asset_id ? 'Claiming...' : 'Claim Boost'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
